// src/lib/twitterAuth.js - OAuth 2.0 PKCE Frontend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

export async function checkTwitterLogin() {
  try {
    console.log('📡 Checking OAuth authentication status...');

    const response = await fetch(`${API_BASE_URL}/api/auth/status`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Auth status:', data.isAuthenticated ? 'Logged in' : 'Not logged in');
      return data.isAuthenticated || false;
    }
    return false;
  } catch (error) {
    console.error('❌ Error checking authentication:', error);
    return false;
  }
}

export async function getTwitterProfile() {
  try {
    console.log('📡 Fetching user profile...');

    const response = await fetch(`${API_BASE_URL}/api/user/profile`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Profile fetched for:', data.username);
      return data;
    } else {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to fetch profile');
    }
  } catch (error) {
    console.error('❌ Error fetching profile:', error);
    throw error;
  }
}

// Start OAuth 2.0 flow - user gets redirected to Twitter automatically
export function initiateTwitterLogin() {
  console.log('🔗 Starting OAuth 2.0 flow...');

  // Save current location to return after auth
  localStorage.setItem('preAuthLocation', window.location.pathname);

  // Redirect to backend OAuth endpoint
  // Backend handles all the OAuth complexity and redirects to Twitter
  window.location.href = `${API_BASE_URL}/auth/twitter`;
}

// Get user's tweets (bonus feature using their access token)
export async function getUserTweets() {
  try {
    console.log('📡 Fetching user tweets...');

    const response = await fetch(`${API_BASE_URL}/api/user/tweets`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Tweets fetched:', data.data?.length || 0, 'tweets');
      return data.data || [];
    } else {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch tweets');
    }
  } catch (error) {
    console.error('❌ Error fetching tweets:', error);
    throw error;
  }
}

export async function logoutTwitter() {
  try {
    console.log('🚪 Logging out...');

    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (response.ok) {
      console.log('✅ Logout successful');
      // Clear any local storage
      localStorage.removeItem('preAuthLocation');
      return true;
    } else {
      throw new Error('Logout failed');
    }
  } catch (error) {
    console.error('❌ Logout error:', error);
    return false;
  }
}

// Handle OAuth return (check URL params for auth result)
export function handleOAuthReturn() {
  const urlParams = new URLSearchParams(window.location.search);
  const authSuccess = urlParams.get('auth');
  const error = urlParams.get('error');
  const message = urlParams.get('message');

  if (authSuccess === 'success') {
    console.log('✅ OAuth authentication successful!');

    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);

    // Optionally redirect to pre-auth location
    const preAuthLocation = localStorage.getItem('preAuthLocation');
    if (preAuthLocation && preAuthLocation !== window.location.pathname) {
      localStorage.removeItem('preAuthLocation');
      window.location.href = preAuthLocation;
    }

    return { success: true };
  }

  if (error) {
    console.log('❌ OAuth authentication failed:', error, message);

    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);

    return {
      success: false,
      error: error,
      message: decodeURIComponent(message || 'Authentication failed')
    };
  }

  return null;
}

// Check if backend is properly configured
export async function checkBackendConfig() {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health`);
    if (response.ok) {
      const data = await response.json();
      return {
        configured: data.oauth?.configured || false,
        clientId: data.oauth?.clientId || false,
        clientSecret: data.oauth?.clientSecret || false
      };
    }
    return { configured: false };
  } catch (error) {
    console.error('❌ Backend config check failed:', error);
    return { configured: false, error: error.message };
  }
}